/**
 * PlatformProviderFormModal Component
 * 
 * Modal for creating/editing platform providers.
 */

import { AppModal } from '@/components/ui/app-modal';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { formResolver } from '@/lib/form-resolver';
import { z } from 'zod';
import { X, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useI18n } from '@/i18n';
import {
  useCreatePlatformProvider,
  usePlatformProviderEndpoint,
  useSupportedProviderTypes,
  useUpdatePlatformProvider,
} from '../hooks/usePlatformProviders';
import { PLATFORM_PROVIDER_TYPES } from '../types';
import type { BedrockDeploymentConfig, PlatformProvider, ProviderType } from '../types';
import { ProviderIcon } from './ProviderIcons';

// =============================================================================
// Form Schema
// =============================================================================

const providerSchema = z
  .object({
    provider_type: z.string().min(1, 'Provider type is required'),
    display_name: z.string().min(1, 'Display name is required').max(100),
    description: z.string().optional(),
    endpoint: z.string().url('Must be a valid URL').optional().or(z.literal('')),
    api_version: z.string().optional(),
    credential_ref: z.string().min(1, 'Credential reference is required'),
    supports_streaming: z.boolean(),
    supports_tools: z.boolean(),
    supports_vision: z.boolean(),
    supports_embeddings: z.boolean(),
    rate_limit_rpm: z.number().optional().nullable(),
    rate_limit_tpm: z.number().optional().nullable(),
    is_active: z.boolean(),
    // Bedrock-specific (deployment_config)
    bedrock_region: z.string().optional(),
    bedrock_auth_mode: z.enum(['access_key', 'iam_role']).optional(),
    bedrock_role_arn: z.string().optional(),
    bedrock_external_id: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.provider_type !== 'aws_bedrock') return true;
      if (!data.bedrock_region || data.bedrock_region.trim() === '') return false;
      if (!data.bedrock_auth_mode) return false;
      if (data.bedrock_auth_mode === 'iam_role' && !data.bedrock_role_arn) return false;
      return true;
    },
    {
      message: 'Region, auth mode and (for iam_role) role ARN are required for AWS Bedrock',
      path: ['bedrock_region'],
    },
  );

type ProviderFormData = z.infer<typeof providerSchema>;

// =============================================================================
// Component
// =============================================================================

interface PlatformProviderFormModalProps {
  provider?: PlatformProvider | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export function PlatformProviderFormModal({
  provider,
  onClose,
  onSuccess,
}: PlatformProviderFormModalProps) {
  const { t } = useI18n();
  const isEditing = !!provider;

  const createMutation = useCreatePlatformProvider();
  const updateMutation = useUpdatePlatformProvider();
  const { data: providerEndpoint } = usePlatformProviderEndpoint(provider?.id ?? '', isEditing);
  const { data: supportedTypesData } = useSupportedProviderTypes();

  // Only offer provider types enabled on this deployment. While the query is
  // loading we fall back to the full static list so the form is usable.
  const supportedValues = supportedTypesData?.provider_types.map((t) => t.value);
  const availableProviderTypes = supportedValues
    ? PLATFORM_PROVIDER_TYPES.filter((t) => supportedValues.includes(t.value))
    : PLATFORM_PROVIDER_TYPES;

  const existingBedrockConfig =
    (provider?.provider_type === 'aws_bedrock'
      ? (provider.deployment_config as Partial<BedrockDeploymentConfig> | undefined)
      : undefined) ?? {};

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm<ProviderFormData>({
    resolver: formResolver(providerSchema),
    defaultValues: {
      provider_type: provider?.provider_type || '',
      display_name: provider?.display_name || '',
      description: provider?.description || '',
      endpoint: '',
      api_version: provider?.api_version || '',
      credential_ref: '', // Never pre-fill for security
      supports_streaming: provider?.supports_streaming ?? true,
      supports_tools: provider?.supports_tools ?? true,
      supports_vision: provider?.supports_vision ?? false,
      supports_embeddings: provider?.supports_embeddings ?? false,
      rate_limit_rpm: provider?.rate_limit_rpm ?? null,
      rate_limit_tpm: provider?.rate_limit_tpm ?? null,
      is_active: provider?.is_active ?? true,
      bedrock_region: existingBedrockConfig.region ?? '',
      bedrock_auth_mode: existingBedrockConfig.auth_mode ?? 'access_key',
      bedrock_role_arn: existingBedrockConfig.role_arn ?? '',
      bedrock_external_id: existingBedrockConfig.external_id ?? '',
    },
  });

  const watchedProviderType = watch('provider_type');
  const watchedAuthMode = watch('bedrock_auth_mode');
  const showBedrockFields = watchedProviderType === 'aws_bedrock';

  useEffect(() => {
    if (provider) {
      const bedrockCfg =
        (provider.provider_type === 'aws_bedrock'
          ? (provider.deployment_config as Partial<BedrockDeploymentConfig> | undefined)
          : undefined) ?? {};
      reset({
        provider_type: provider.provider_type,
        display_name: provider.display_name,
        description: provider.description || '',
        endpoint: providerEndpoint?.endpoint || '',
        api_version: provider.api_version || '',
        credential_ref: '', // Never pre-fill for security
        supports_streaming: provider.supports_streaming,
        supports_tools: provider.supports_tools,
        supports_vision: provider.supports_vision,
        supports_embeddings: provider.supports_embeddings,
        rate_limit_rpm: provider.rate_limit_rpm,
        rate_limit_tpm: provider.rate_limit_tpm,
        is_active: provider.is_active,
        bedrock_region: bedrockCfg.region ?? '',
        bedrock_auth_mode: bedrockCfg.auth_mode ?? 'access_key',
        bedrock_role_arn: bedrockCfg.role_arn ?? '',
        bedrock_external_id: bedrockCfg.external_id ?? '',
      });
    }
  }, [provider, providerEndpoint, reset]);

  const onSubmit = async (data: ProviderFormData) => {
    // Extract bedrock_* form fields into deployment_config for AWS Bedrock;
    // other providers keep deployment_config undefined so the backend keeps
    // its existing value.
    const {
      bedrock_region,
      bedrock_auth_mode,
      bedrock_role_arn,
      bedrock_external_id,
      ...rest
    } = data;

    const deployment_config: Record<string, unknown> | undefined =
      data.provider_type === 'aws_bedrock'
        ? {
            region: bedrock_region?.trim(),
            auth_mode: bedrock_auth_mode ?? 'access_key',
            ...(bedrock_auth_mode === 'iam_role' && bedrock_role_arn
              ? { role_arn: bedrock_role_arn.trim() }
              : {}),
            ...(bedrock_external_id?.trim()
              ? { external_id: bedrock_external_id.trim() }
              : {}),
          }
        : undefined;

    try {
      if (isEditing && provider) {
        await updateMutation.mutateAsync({
          id: provider.id,
          data: {
            ...rest,
            // Only include credential_ref if it was changed
            credential_ref: rest.credential_ref || undefined,
            rate_limit_rpm: rest.rate_limit_rpm || undefined,
            rate_limit_tpm: rest.rate_limit_tpm || undefined,
            ...(deployment_config ? { deployment_config } : {}),
          },
        });
      } else {
        await createMutation.mutateAsync({
          ...rest,
          provider_type: rest.provider_type as ProviderType,
          rate_limit_rpm: rest.rate_limit_rpm || undefined,
          rate_limit_tpm: rest.rate_limit_tpm || undefined,
          ...(deployment_config ? { deployment_config } : {}),
        });
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to save provider:', error);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending || isSubmitting;

  return (
    <AppModal
      onClose={onClose}
      ariaLabel={isEditing ? t('admin.providers.editTitle') : t('admin.providers.addTitle')}
      testId="provider-form-modal"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {isEditing ? t('admin.providers.editTitle') : t('admin.providers.addTitle')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
          {/* Provider Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('admin.providers.providerType')}
            </label>
            <div className="grid grid-cols-1 gap-2">
              {availableProviderTypes.map((type) => {
                const selected = watch('provider_type') === type.value;
                return (
                  <label
                    key={type.value}
                    className={`flex items-center gap-3 px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
                      isEditing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    } ${
                      selected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-600'
                    }`}
                  >
                    <input
                      type="radio"
                      value={type.value}
                      disabled={isEditing}
                      {...register('provider_type')}
                      className="sr-only"
                    />
                    <div className="w-7 h-7 rounded flex items-center justify-center bg-gray-100 dark:bg-gray-700 flex-shrink-0">
                      <ProviderIcon providerType={type.value} size={18} />
                    </div>
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{type.label}</span>
                  </label>
                );
              })}
            </div>
            {errors.provider_type && (
              <p className="text-sm text-red-500 mt-1">{errors.provider_type.message}</p>
            )}
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('admin.providers.displayName')}
            </label>
            <Input
              {...register('display_name')}
              placeholder={t('admin.providers.displayNamePlaceholder')}
            />
            {errors.display_name && (
              <p className="text-sm text-red-500 mt-1">{errors.display_name.message}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('admin.providers.description')}
            </label>
            <Input
              {...register('description')}
              placeholder={t('admin.providers.descriptionPlaceholder')}
            />
          </div>

          {/* Endpoint */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('admin.providers.endpoint')}
            </label>
            <Input {...register('endpoint')} placeholder={t('admin.providers.endpointPlaceholder')} />
            {errors.endpoint && (
              <p className="text-sm text-red-500 mt-1">{errors.endpoint.message}</p>
            )}
          </div>

          {/* API Version */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('admin.providers.apiVersion')}
            </label>
            <Input {...register('api_version')} placeholder={t('admin.providers.apiVersionPlaceholder')} />
          </div>

          {/* AWS Bedrock — provider-specific deployment config */}
          {showBedrockFields && (
            <div className="border-t dark:border-gray-700 pt-4 space-y-3">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {t('admin.providers.bedrockConfigTitle')}
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('admin.providers.bedrockRegion')}
                </label>
                <Input {...register('bedrock_region')} placeholder={t('admin.providers.bedrockRegionPlaceholder')} />
                {errors.bedrock_region && (
                  <p className="text-sm text-red-500 mt-1">{errors.bedrock_region.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('admin.providers.bedrockAuthMode')}
                </label>
                <select
                  {...register('bedrock_auth_mode')}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600"
                >
                  <option value="access_key">{t('admin.providers.bedrockAuthStatic')}</option>
                  <option value="iam_role">{t('admin.providers.bedrockAuthIAM')}</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {t('admin.providers.bedrockCredsHelp')}
                </p>
              </div>

              {watchedAuthMode === 'iam_role' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('admin.providers.bedrockRoleArn')}
                    </label>
                    <Input {...register('bedrock_role_arn')} placeholder={t('admin.providers.bedrockRoleArnPlaceholder')} />
                    {errors.bedrock_role_arn && (
                      <p className="text-sm text-red-500 mt-1">{errors.bedrock_role_arn.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('admin.providers.bedrockExternalId')}
                    </label>
                    <Input {...register('bedrock_external_id')} placeholder={t('admin.providers.bedrockExternalIdPlaceholder')} />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Credential Reference */}
          <div className="border-t dark:border-gray-700 pt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('admin.providers.credentialRef')}
            </label>
            <Input {...register('credential_ref')} placeholder={t('admin.providers.credentialPlaceholder')} />
            <p className="text-xs text-gray-500 mt-1">
              {isEditing
                ? t('admin.providers.credentialHelpEdit')
                : t('admin.providers.credentialHelpCreate')
              }
            </p>
            {errors.credential_ref && (
              <p className="text-sm text-red-500 mt-1">{errors.credential_ref.message}</p>
            )}
          </div>

          {/* Capabilities */}
          <div className="border-t dark:border-gray-700 pt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.providers.capabilities')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">
                <input type="checkbox" {...register('supports_streaming')} className="rounded border-gray-300" />
                <span className="text-sm">{t('admin.providers.capabilityStreaming')}</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" {...register('supports_tools')} className="rounded border-gray-300" />
                <span className="text-sm">{t('admin.providers.capabilityTools')}</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" {...register('supports_vision')} className="rounded border-gray-300" />
                <span className="text-sm">{t('admin.providers.capabilityVision')}</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" {...register('supports_embeddings')} className="rounded border-gray-300" />
                <span className="text-sm">{t('admin.providers.capabilityEmbeddings')}</span>
              </label>
            </div>
          </div>

          {/* Rate Limits */}
          <div className="border-t dark:border-gray-700 pt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('admin.providers.rateLimits')}
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500">{t('admin.providers.rateLimitsRpm')}</label>
                <Input type="number" {...register('rate_limit_rpm', { valueAsNumber: true })} placeholder="1000" />
              </div>
              <div>
                <label className="text-xs text-gray-500">{t('admin.providers.rateLimitsTpm')}</label>
                <Input type="number" {...register('rate_limit_tpm', { valueAsNumber: true })} placeholder="100000" />
              </div>
            </div>
          </div>

          {/* Active */}
          <div className="border-t dark:border-gray-700 pt-4">
            <label className="flex items-center gap-2">
              <input type="checkbox" {...register('is_active')} className="rounded border-gray-300" />
              <span className="text-sm font-medium">{t('admin.providers.active')}</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              {t('admin.providers.activeHelp')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t dark:border-gray-700">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('admin.providers.cancel')}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEditing ? t('admin.providers.saveChanges') : t('admin.providers.createProvider')}
            </Button>
          </div>
        </form>
      </div>
    </AppModal>
  );
}
